import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiAward, FiUsers, FiClock, FiX, FiCheckCircle } from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import DepositModal from '../../components/DepositModal/DepositModal';
import KycBanner from '../../components/KycBanner/KycBanner';
import styles from './PartnerProgram.module.css';
import axiosClient from '../../api/axiosClient';

interface Tier {
  key: string; name: string; investCapital: number; seats: number; seatsFilled: number; seatsAvailable: number;
  programYears: number; exitLockYears: number; noticeDays: number; growth: string; equityTotalPct: number; equityNote: string;
}
interface Application {
  id: string; tier: string; status: string; created_at: string; message: string | null;
}

const STATUS_COLORS: Record<string, string> = { pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a' };

const PartnerProgram: React.FC = () => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [raiseTargetUsd, setRaiseTargetUsd] = useState<number | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [viewingApp, setViewingApp] = useState<Application | null>(null);
  const [payTier, setPayTier] = useState<string | null>(null);

  const load = () => {
    Promise.all([
      axiosClient.get('/partners/tiers'),
      axiosClient.get('/partners/mine'),
    ]).then(([t, a]) => {
      setTiers(t.data.tiers);
      if (t.data.raiseTargetUsd) setRaiseTargetUsd(Number(t.data.raiseTargetUsd));
      setApplications(a.data.applications);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submitApplication = async () => {
    if (!selectedTier) return;
    setApplying(selectedTier);
    try {
      await axiosClient.post('/partners/apply', { tier: selectedTier, message });
      toast.success('Application submitted — our team will reach out shortly.');
      setSelectedTier(null);
      setMessage('');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not submit application.');
    } finally {
      setApplying(null);
    }
  };

  return (
    <DashboardShell>
      <PageHeader
        title="Institutional Partner Program"
        subtitle={`${raiseTargetUsd ? `$${raiseTargetUsd >= 1000000 ? `${(raiseTargetUsd / 1000000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M` : `${Math.round(raiseTargetUsd / 1000)}K`}` : '$4M'} institutional funds raising — four partner tiers with equity and priority terms.`}
        action={<WalletBadge />}
      />

      <div className={styles.page}>
        <KycBanner />
        <div className={styles.commissionBanner}>
          <h3>Institutional Partners Introducing Commission</h3>
          <p>Enjoying your institutional Partners Introducing Commission — flat 2.5% on every selected partner's positions.</p>
        </div>
        <div className={styles.tierGrid}>
          {tiers.map((t) => (
            <div key={t.key} className={styles.tierCard}>
              <div className={styles.tierHeader}>
                <FiAward />
                <h3>{t.name}</h3>
              </div>
              <div className={styles.tierAmount}>${t.investCapital.toLocaleString()}</div>
              <div className={styles.seatsRow}>
                <FiUsers /> {t.seatsAvailable} of {t.seats} seats available
              </div>
              <ul className={styles.detailList}>
                <li><span>Program</span><b>{t.programYears} years</b></li>
                <li><span>Exit lock-in</span><b>{t.exitLockYears} years ({t.noticeDays}-day notice)</b></li>
                <li><span>Growth</span><b>{t.growth}</b></li>
                <li><span>Equity share</span><b>{t.equityTotalPct}%</b></li>
              </ul>
              <div className={styles.equityNote}>{t.equityNote}</div>
              <button
                className={styles.applyBtn}
                disabled={t.seatsAvailable === 0}
                onClick={() => setSelectedTier(t.key)}
              >
                {t.seatsAvailable === 0 ? 'Seats Full' : 'Apply for this Tier'}
              </button>
            </div>
          ))}
        </div>

        {selectedTier && (
          <div className={styles.overlay} onClick={() => setSelectedTier(null)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3>Apply — {tiers.find((t) => t.key === selectedTier)?.name}</h3>
              <p className={styles.modalHint}>Our team will follow up using your account email and phone on file.</p>
              <textarea
                placeholder="Anything you'd like us to know (optional)"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className={styles.modalActions}>
                <button className={styles.cancelBtn} onClick={() => setSelectedTier(null)}>Cancel</button>
                <button className={styles.confirmBtn} onClick={submitApplication} disabled={applying === selectedTier}>
                  {applying === selectedTier ? 'Submitting…' : 'Submit Application'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.card}>
          <h3>Your Applications</h3>
          {loading && <div className={styles.muted}>Loading…</div>}
          {!loading && applications.length === 0 && <div className={styles.muted}>No applications yet — apply to a tier above.</div>}
          {!loading && applications.length > 0 && (
            <div className={styles.list}>
              {applications.map((a) => (
                <div key={a.id} className={styles.listRow} onClick={() => setViewingApp(a)} style={{ cursor: 'pointer' }}>
                  <div>
                    <b>{a.tier} Partner</b>
                    <div className={styles.listSub}><FiClock /> {new Date(a.created_at).toLocaleString()}</div>
                  </div>
                  <span className={styles.statusPill} style={{ color: STATUS_COLORS[a.status], borderColor: STATUS_COLORS[a.status] }}>{a.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {viewingApp && (
          <div className={styles.overlay} onClick={() => setViewingApp(null)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h3>{viewingApp.tier} Partner Application</h3>
                <button onClick={() => setViewingApp(null)} style={{ background: 'var(--hover-bg)', border: '1px solid var(--line)', color: 'var(--muted)', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX /></button>
              </div>
              <div className={styles.modalDetailList}>
                <div><span>Status</span><span className={styles.statusPill} style={{ color: STATUS_COLORS[viewingApp.status], borderColor: STATUS_COLORS[viewingApp.status] }}>{viewingApp.status}</span></div>
                <div><span>Submitted</span><b>{new Date(viewingApp.created_at).toLocaleString()}</b></div>
                {viewingApp.message && <div><span>Message</span><b>{viewingApp.message}</b></div>}
              </div>

              {viewingApp.status === 'approved' && (
                <>
                  <p className={styles.modalHint} style={{ marginTop: 12 }}>
                    Your application has been approved — you can complete your partner investment payment below.
                  </p>
                  <button
                    className={styles.applyBtn}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    onClick={() => { setPayTier(viewingApp.tier); setViewingApp(null); }}
                  >
                    <FiCheckCircle /> Pay Now
                  </button>
                </>
              )}
              {viewingApp.status === 'pending' && (
                <p className={styles.modalHint} style={{ marginTop: 12 }}>Our team is reviewing your application — you'll be able to pay once it's approved.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {payTier && <DepositModal initialPartnerTier={payTier} onClose={() => setPayTier(null)} onSuccess={load} />}
    </DashboardShell>
  );
};

export default PartnerProgram;
